import { useCallback, useEffect, useRef, useState } from "react"
import { connect, createLocalTracks, isSupported, LocalVideoTrack } from "twilio-video"
import SyncClient from "twilio-sync"

import collapseAndroidCameras from "./cameraList"
import { csrfToken } from "./orderActions"

// useVideoRoom — the Twilio transport for the React video-call surface, a 1:1
// port of the legacy Stimulus controller (app/frontend/controllers/
// video_chats_controller.js, 605 lines). It owns device enumeration/preview, the
// waiting-room Sync subscription, the Twilio Video room connect + participant
// lifecycle, mic/cam/screen toggles, the countdown, the recording + close
// beacons, and the phase state machine. VideoCall.jsx stays declarative and reads
// this hook's state.
//
// Phases: prejoin → (waiting →) incall → ended, plus `limit` (entered only from
// server truth). `chat`/`screen` are overlays the page layers on incall — the
// Twilio tracks never unmount when toggling them. Real dev Twilio services back
// this locally (creds present); it is verified single-context behind an env gate.
//
// Transport endpoints are reused as-is (session-auth, never /api/v1): POST
// /video_chats (manager instant-start), GET /video_chats/:id (join/show), POST
// /video_chat_waiting_rooms (host waiting room), POST /video_chats/close and
// /video_chats/start_recording (beacons). The side-chat handle (conversationSid)
// arrives on the Sync-doc update (host) or the show/create JSON (join/rejoin).

const DEFAULT_ID = "default"

// Twilio's disconnect code when the same identity connects to the room again.
const DUPLICATE_IDENTITY = 53205

// Legacy `createSession` confirm copy — kept verbatim for the manager instant path.
const INSTANT_START_CONFIRM =
  "This will start a video chat and use 1 daily room. Consider using the waiting room feature instead from a DM conversation. Do you want to continue?"

// Format seconds → "MM:SS", matching the legacy makeTimer padding.
function formatTimeLeft(seconds) {
  if (seconds == null || seconds <= 0) return "0:00"
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs < 10 ? "0" : ""}${secs}`
}

export default function useVideoRoom({ initialLimitReached = false } = {}) {
  const [supported] = useState(() => isSupported)
  // Phones ship no screen-capture API at all — getDisplayMedia is absent on iOS
  // Safari and Android Chrome — so a Share control there can only ever fail.
  // The page hides the control on this flag instead of rendering a dead button
  // (AU-21). Watching a screen someone else shares needs no API and still works.
  const [canScreenShare] = useState(() => typeof navigator.mediaDevices?.getDisplayMedia === "function")
  const [phase, setPhase] = useState(initialLimitReached ? "limit" : "prejoin")

  // Devices + selection. Default to the OS-preferred devices so the prejoin
  // surface is useful on first load; users can still explicitly choose the
  // no-audio/no-camera options.
  const [mics, setMics] = useState([])
  const [cameras, setCameras] = useState([])
  const [speakers, setSpeakers] = useState([])
  const [micId, setMicId] = useState("")
  const [camId, setCamId] = useState("")
  const [speakerId, setSpeakerId] = useState("")
  const [micDenied, setMicDenied] = useState(false)
  const [cameraError, setCameraError] = useState(null)

  const [error, setError] = useState(null)
  const [roomCode, setRoomCode] = useState(null)
  const [conversationSid, setConversationSid] = useState(null)

  // In-call reactive state.
  const [micOn, setMicOn] = useState(true)
  const [camOn, setCamOn] = useState(true)
  const [screenOn, setScreenOn] = useState(false)
  const [localVideoTrack, setLocalVideoTrack] = useState(null)
  const [remoteScreenTrack, setRemoteScreenTrack] = useState(null)
  const [participants, setParticipants] = useState([]) // remote participants
  const [previewTrack, setPreviewTrack] = useState(null)
  const [endTime, setEndTime] = useState(null)
  const [timeLeft, setTimeLeft] = useState(null)
  const [endedSummary, setEndedSummary] = useState(null)
  const [starting, setStarting] = useState(false)

  const roomRef = useRef(null)
  const syncDocRef = useRef(null)
  const syncClientRef = useRef(null)
  const syncExpiryRef = useRef(null)
  const connectedAtRef = useRef(null)
  const startingRef = useRef(false)

  // The Sync transport exists only to deliver the room-start signal to a waiting
  // host. Once it has done that — or the wait ends any other way — it must be torn
  // down completely: its token lives 600s (Twilio::Sync TTL), and a surviving
  // `tokenExpired` listener would fire mid-call and reset the UI to prejoin while
  // the Video room stays connected (AT-09). Idempotent; safe to call at any phase.
  const disposeSync = useCallback(() => {
    const client = syncClientRef.current
    const expiry = syncExpiryRef.current
    syncDocRef.current?.close?.()
    if (client && expiry) client.removeListener?.("tokenExpired", expiry)
    // shutdown() is async and we never await it — nothing downstream depends on the
    // socket being closed, and a rejection here must not break the join transition.
    client?.shutdown?.()?.catch?.(() => {})
    syncDocRef.current = null
    syncClientRef.current = null
    syncExpiryRef.current = null
  }, [])

  // ---- device enumeration (Stimulus connect() 6–51) ----------------------
  const initDevices = useCallback(async () => {
    if (!isSupported) return
    try {
      if (!navigator.userAgent.toLowerCase().includes("firefox")) {
        try {
          const result = await navigator.permissions.query({ name: "microphone" })
          if (result.state === "denied") setMicDenied(true)
        } catch {
          /* permissions API unavailable — fall through to getUserMedia */
        }
      }

      await navigator.mediaDevices.getUserMedia({ audio: true })
      let devices = await navigator.mediaDevices.enumerateDevices()

      const nextMics = devices.filter((d) => d.kind === "audioinput")
      const nextSpeakers = devices.filter((d) => d.kind === "audiooutput")
      setMics(nextMics)
      setSpeakers(nextSpeakers)
      // Default to the OS default input/output, like the legacy select markup.
      const defMic = nextMics.find((d) => d.deviceId === DEFAULT_ID) || nextMics[0]
      if (defMic) setMicId(defMic.deviceId)
      const defSpk = nextSpeakers.find((d) => d.deviceId === DEFAULT_ID) || nextSpeakers[0]
      if (defSpk) setSpeakerId(defSpk.deviceId)

      await navigator.mediaDevices.getUserMedia({ video: true })
      devices = await navigator.mediaDevices.enumerateDevices()
      // Enumeration lists lenses, not usable cameras: a multi-lens Android phone
      // reports four where its owner has two (AT-14).
      const nextCameras = collapseAndroidCameras(devices.filter((d) => d.kind === "videoinput"))
      setCameras(nextCameras)
      const defCam = nextCameras.find((d) => d.deviceId === DEFAULT_ID) || nextCameras[0]
      if (defCam) setCamId(defCam.deviceId)
    } catch {
      // Mic/camera blocked or absent — leave selections empty. The prejoin gate
      // ("No Audio ⇒ hide join") then blocks the call, matching the legacy flow.
      setMicDenied(true)
    }
  }, [])

  // ---- camera preview in prejoin (previewCamera 512–539) ------------------
  useEffect(() => {
    if (phase !== "prejoin") return undefined
    let cancelled = false
    let track = null

    if (!camId) {
      setPreviewTrack(null)
      return undefined
    }

    createLocalTracks({ audio: false, video: { deviceId: camId } })
      .then((tracks) => {
        if (cancelled) {
          tracks.forEach((t) => t.stop())
          return
        }
        track = tracks.find((t) => t.kind === "video") || null
        setPreviewTrack(track)
        setCameraError(null)
      })
      .catch(() => {
        if (cancelled) return
        // Enumeration is not proof of operability: a listed camera can fail to
        // open. Clearing the preview alone left the customer staring at a blank
        // frame with no explanation, and still handed the dead device id to
        // `connect`, so the call itself started without video too. Say so, and
        // drop the selection so nothing forwards it (AT-14).
        setPreviewTrack(null)
        setCamId("")
        setCameraError("That camera didn't start. Pick another one, or continue without video.")
      })

    return () => {
      cancelled = true
      if (track) track.stop()
      setPreviewTrack(null)
    }
  }, [camId, phase])

  const noAudio = !micId

  // ---- shared: connect to a Twilio Video room (createRoom 119–189) ---------
  const connectRoom = useCallback(
    async (token, room, roomEndTime) => {
      setRoomCode(room)
      setEndTime(roomEndTime)

      const audio = micId ? { deviceId: micId } : false
      const video = camId ? { deviceId: camId } : false

      // Stop the prejoin preview before connecting (legacy previewCamera.stop()).
      if (previewTrack) {
        previewTrack.stop()
        setPreviewTrack(null)
      }

      try {
        const twilioRoom = await connect(token, { name: room, audio, video })
        roomRef.current = twilioRoom
        connectedAtRef.current = Date.now()
        setPhase("incall")

        // Initial mute/camera-off when the device was empty (legacy toggles).
        setMicOn(Boolean(micId))
        setCamOn(Boolean(camId))

        // Local video track (for the self tile).
        const localVid = Array.from(twilioRoom.localParticipant.videoTracks.values())[0]?.track || null
        setLocalVideoTrack(localVid)

        twilioRoom.participants.forEach(handleParticipant)
        twilioRoom.on("participantConnected", handleParticipant)
        twilioRoom.on("participantDisconnected", handleParticipantDisconnected)
        twilioRoom.on("disconnected", (_room, disconnectError) => finalize(twilioRoom, disconnectError))

        // Fire the server-authoritative recording beacon (noop off production).
        const data = new FormData()
        data.append("id", room)
        navigator.sendBeacon("/video_chats/start_recording", data)
      } catch {
        setError("Unable to connect to the room — please verify your room code.")
        setPhase("prejoin")
      }
    },
    // handleParticipant / finalize are stable (defined below via refs).
    [micId, camId, previewTrack] // eslint-disable-line react-hooks/exhaustive-deps
  )

  // ---- participant lifecycle (handleConnectedParticipant 212–299) ----------
  const upsertParticipant = useCallback((sid, patch) => {
    setParticipants((prev) => {
      const idx = prev.findIndex((p) => p.sid === sid)
      if (idx === -1) return [...prev, { sid, ...patch }]
      const next = [...prev]
      next[idx] = { ...next[idx], ...patch }
      return next
    })
  }, [])

  const bindTrack = useCallback(
    (participant, track) => {
      if (track.name === "screen") {
        setRemoteScreenTrack(track)
        setScreenOn(true)
        return
      }
      if (track.kind === "video") {
        upsertParticipant(participant.sid, { videoTrack: track, cameraOff: !track.isEnabled })
        track.on("disabled", () => upsertParticipant(participant.sid, { cameraOff: true }))
        track.on("enabled", () => upsertParticipant(participant.sid, { cameraOff: false }))
      } else if (track.kind === "audio") {
        upsertParticipant(participant.sid, { audioTrack: track, muted: !track.isEnabled })
        track.on("disabled", () => upsertParticipant(participant.sid, { muted: true }))
        track.on("enabled", () => upsertParticipant(participant.sid, { muted: false }))
      }
    },
    [upsertParticipant]
  )

  const handleParticipant = useCallback(
    (participant) => {
      upsertParticipant(participant.sid, {
        sid: participant.sid,
        identity: participant.identity,
        videoTrack: null,
        audioTrack: null,
        cameraOff: true,
        muted: false,
      })

      participant.tracks.forEach((publication) => {
        if (publication.isSubscribed && publication.track) bindTrack(participant, publication.track)
      })
      participant.on("trackSubscribed", (track) => bindTrack(participant, track))
      participant.on("trackUnpublished", (publication) => {
        if (publication.trackName === "screen") {
          setRemoteScreenTrack(null)
          setScreenOn(false)
        }
      })
    },
    [bindTrack, upsertParticipant]
  )

  const handleParticipantDisconnected = useCallback((participant) => {
    setParticipants((prev) => prev.filter((p) => p.sid !== participant.sid))
    // A hard disconnect may not fire trackUnpublished, so clear any screen share
    // the leaver was presenting (safe in 1:1 — the only remote is the one leaving).
    setRemoteScreenTrack(null)
    setScreenOn(false)
    participant.removeAllListeners?.()
  }, [])

  // ---- teardown + ended summary (disconnect/completeRoom 398–470) ----------
  //
  // Only the connection this page currently holds may end the call; a late
  // "disconnected" from one it already let go of is ignored. A connection Twilio
  // dropped because the same person joined again elsewhere (53205) must not
  // close the room, or it ends the call for the connection that replaced it.
  const finalize = useCallback((room, error) => {
    if (room !== roomRef.current) return

    const startedAt = connectedAtRef.current
    const seconds = startedAt ? Math.round((Date.now() - startedAt) / 1000) : 0
    const replaced = error?.code === DUPLICATE_IDENTITY

    if (room && room.participants.size === 0 && !replaced) {
      const data = new FormData()
      data.append("id", room.name)
      navigator.sendBeacon("/video_chats/close", data)
    }

    setEndedSummary({ seconds })
    setPhase("ended")
    setScreenOn(false)
    setParticipants([])
    setRemoteScreenTrack(null)
    setLocalVideoTrack(null)
    roomRef.current = null
    connectedAtRef.current = null
  }, [])

  const leave = useCallback(() => {
    const room = roomRef.current
    if (room) room.disconnect()
    finalize(room)
  }, [finalize])

  // ---- one start at a time -------------------------------------------------
  // A second click on a start or join control while the first request is still
  // in flight opens a second waiting room, or joins the same person twice and
  // Twilio drops the first connection (53205). Every start path claims this
  // first and releases it when its request settles; the page disables its
  // controls while `starting`.
  const claimStart = useCallback(() => {
    if (startingRef.current || roomRef.current) return false
    startingRef.current = true
    setStarting(true)
    return true
  }, [])

  const releaseStart = useCallback(() => {
    startingRef.current = false
    setStarting(false)
  }, [])

  // ---- host: waiting room (createWaitingRoom + subscribe 554–604) ----------
  const startWaitingRoom = useCallback(
    async (partnerUserId, origin = null) => {
      if (!claimStart()) return
      setError(null)
      // Never stack a second waiting session on top of a live one.
      disposeSync()
      try {
        const res = await fetch("/video_chat_waiting_rooms", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": csrfToken() },
          body: JSON.stringify({
            chat_partner: partnerUserId,
            conversation_sid: origin?.conversationSid,
            order_id: origin?.orderId,
          }),
        })
        const json = await res.json().catch(() => null)
        if (!res.ok) {
          setError(json?.error || "Unable to start the meeting.")
          return
        }

        setRoomCode(json.room)
        setPhase("waiting")

        const client = new SyncClient(json.sync)
        syncClientRef.current = client
        const doc = await client.document(json.room)
        // Cancel/unmount during the open above already disposed this client; do not
        // reinstall a document onto a transport nobody owns any more.
        if (syncClientRef.current !== client) {
          doc.close()
          return
        }
        syncDocRef.current = doc

        // One-shot: the room starts once. Tear the whole transport down before
        // connecting, so nothing Sync-side can still speak once we are in the call.
        doc.once("updated", (event) => {
          const data = event?.data || {}
          if (data.conversationSid) setConversationSid(data.conversationSid)
          disposeSync()
          connectRoom(json.token, json.room, data.endTime)
        })

        const onTokenExpired = () => {
          // Only the transport still owning this wait may end it. If the room has
          // started, this client was already disposed (or a Video room is up), so the
          // expiry is about a wait that is over — on that race, the room start wins.
          if (syncClientRef.current !== client || roomRef.current) return
          setError("The waiting room has timed out.")
          setPhase("prejoin")
          disposeSync()
        }
        syncExpiryRef.current = onTokenExpired
        client.on("tokenExpired", onTokenExpired)
      } catch {
        setError("Unable to start the meeting.")
        setPhase("prejoin")
      } finally {
        releaseStart()
      }
    },
    [connectRoom, disposeSync, claimStart, releaseStart]
  )

  const cancelWaiting = useCallback(() => {
    disposeSync()
    setPhase("prejoin")
    setRoomCode(null)
  }, [disposeSync])

  // ---- guest/rejoin: join by code (joinSession 93–117) ---------------------
  const joinWithCode = useCallback(
    async (code) => {
      const room = (code || "").trim()
      if (!room || !claimStart()) return
      setError(null)
      try {
        const res = await fetch(`/video_chats/${encodeURIComponent(room)}`, {
          method: "GET",
          headers: { Accept: "application/json" },
        })
        const json = await res.json().catch(() => null)
        if (!res.ok || !json) {
          setError("Unable to join room. It may no longer exist.")
          return
        }
        if (json.conversationSid) setConversationSid(json.conversationSid)
        await connectRoom(json.token, json.room, json.endTime)
      } catch {
        setError("Unable to join room. It may no longer exist.")
      } finally {
        releaseStart()
      }
    },
    [connectRoom, claimStart, releaseStart]
  )

  // ---- manager: instant start (createSession 53–83) ------------------------
  const createInstant = useCallback(
    async (partnerUserId = "") => {
      // Legacy manager-only parity: this instant path burns a daily room, so keep
      // the confirm gate the Stimulus `createSession` had before starting one.
      if (typeof window !== "undefined" && window.confirm && !window.confirm(INSTANT_START_CONFIRM)) return
      if (!claimStart()) return
      setError(null)
      try {
        const res = await fetch("/video_chats", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": csrfToken() },
          body: JSON.stringify({ chat_partner: partnerUserId }),
        })
        const json = await res.json().catch(() => null)
        if (!res.ok) {
          setError(json?.error || "Unable to start the meeting.")
          return
        }
        if (json.conversationSid) setConversationSid(json.conversationSid)
        await connectRoom(json.token, json.room, json.endTime)
      } catch {
        setError("Unable to start the meeting.")
      } finally {
        releaseStart()
      }
    },
    [connectRoom, claimStart, releaseStart]
  )

  // ---- toggles (toggleAudio/Video/ScreenShare 301–396) ---------------------
  const toggleMic = useCallback(() => {
    const room = roomRef.current
    if (!room) return
    room.localParticipant.audioTracks.forEach(({ track }) => {
      if (track.isEnabled) track.disable()
      else track.enable()
      setMicOn(track.isEnabled)
    })
  }, [])

  const toggleCam = useCallback(() => {
    const room = roomRef.current
    if (!room) return
    room.localParticipant.videoTracks.forEach(({ track }) => {
      if (track.name === "screen") return
      if (track.isEnabled) track.disable()
      else track.enable()
      setCamOn(track.isEnabled)
    })
  }, [])

  const toggleScreenShare = useCallback(async () => {
    const room = roomRef.current
    if (!room) return

    // Already sharing locally → stop + unpublish (legacy guard).
    let sharingLocal = null
    room.localParticipant.videoTracks.forEach((publication) => {
      if (publication.track.name === "screen") sharingLocal = publication
    })
    if (sharingLocal) {
      sharingLocal.track.stop()
      sharingLocal.unpublish()
      setScreenOn(false)
      return
    }

    // A remote is already sharing — block (legacy alert).
    let remoteSharing = false
    room.participants.forEach((participant) => {
      participant.videoTracks.forEach((publication) => {
        if (publication.track?.name === "screen") remoteSharing = true
      })
    })
    if (remoteSharing) {
      setError("Screen share already in progress.")
      return
    }

    try {
      const stream = await navigator.mediaDevices.getDisplayMedia()
      const screenTrack = new LocalVideoTrack(stream.getTracks()[0], { name: "screen" })
      room.localParticipant.publishTrack(screenTrack)
      setScreenOn(true)
      // Stop sharing when the browser's native "stop sharing" ends the track.
      stream.getTracks()[0].addEventListener("ended", () => {
        try {
          screenTrack.stop()
          room.localParticipant.unpublishTrack(screenTrack)
        } catch {
          /* room already gone */
        }
        setScreenOn(false)
      })
    } catch {
      setError("Could not share the screen.")
    }
  }, [])

  const restart = useCallback(() => {
    setEndedSummary(null)
    setError(null)
    setPhase("prejoin")
    setConversationSid(null)
    setRoomCode(null)
  }, [])

  // ---- countdown (makeTimer 472–492) --------------------------------------
  useEffect(() => {
    if (phase !== "incall" || !endTime) return undefined
    const target = Date.parse(endTime) / 1000
    const tick = () => {
      const now = Date.now() / 1000
      const left = Math.max(0, target - now)
      setTimeLeft(left)
      if (left <= 0) leave()
    }
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [phase, endTime, leave])

  // ---- close beacon on tab close (completeRoom on pagehide) ---------------
  useEffect(() => {
    const onPageHide = () => {
      const room = roomRef.current
      if (!room) return
      // Only finalize (send the close beacon) when we are the last participant,
      // matching finalize()/the legacy completeRoom — otherwise closing one tab
      // would mark the room completed while the other participant is still in it.
      if (room.participants.size === 0) {
        const data = new FormData()
        data.append("id", room.name)
        navigator.sendBeacon("/video_chats/close", data)
      }
      room.disconnect()
    }
    window.addEventListener("pagehide", onPageHide)
    return () => window.removeEventListener("pagehide", onPageHide)
  }, [])

  // Unmount cleanup — never orphan a live room or Sync subscription.
  useEffect(
    () => () => {
      disposeSync()
      roomRef.current?.disconnect?.()
    },
    [disposeSync]
  )

  return {
    supported,
    canScreenShare,
    phase,
    // devices
    mics,
    cameras,
    speakers,
    micId,
    camId,
    speakerId,
    setMicId,
    setCamId,
    setSpeakerId,
    micDenied,
    cameraError,
    noAudio,
    previewTrack,
    // call state
    micOn,
    camOn,
    screenOn,
    localVideoTrack,
    remoteScreenTrack,
    participants,
    roomCode,
    conversationSid,
    setConversationSid,
    endTime,
    timeLeft,
    timeLeftLabel: formatTimeLeft(timeLeft),
    endedSummary,
    starting,
    error,
    setError,
    // actions
    initDevices,
    startWaitingRoom,
    cancelWaiting,
    joinWithCode,
    createInstant,
    toggleMic,
    toggleCam,
    toggleScreenShare,
    leave,
    restart,
  }
}
